import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import classNames from 'classnames';
import {InfoWidgetItem, InfoWidgetValueItem, isInfoWidgetDatapoint, isInfoWidgetProperty} from './types';
import {InfoWidgetItemCategory} from './types';
import {NO_VALUE_DISPLAY, translateCustomTokens} from './InfoWidgetUtils';
import {getItemIdentifier, getItemKey} from './config/ConfigUtils';
import InfoWidgetPaginated from './InfoWidgetPaginated';
import InfoWidgetRegular from './InfoWidgetRegular';
import type {TableItem} from './types';
import './InfoWidget.less';
import {Asset} from '@deviceinsight/ng-ui-api-client';
import {WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import {useAssetSubscription} from '../common/useAssetSubscription';
import React from 'react';
import {nonNull} from '../../../utils/tsUtils';

type State = {
	valueItems: Array<InfoWidgetValueItem>;
	paginatedItems: Array<TableItem>;
};

const InfoWidget: React.FC<WidgetProps> = ({config, context}) => {
	const {t} = useTranslation();
	const [state, setState] = React.useState<State>({
		valueItems: [],
		paginatedItems: []
	});

	const asset = context.type === 'asset' ? context.asset : null;

	const datapointNames = React.useMemo(
		() =>
			(config.settings.items as InfoWidgetItem[])
				.filter(isInfoWidgetDatapoint)
				.map((item) => item.datapoint)
				.filter(nonNull)
				.map((datapoint) => datapoint.name),
		[config.settings.items]
	);

	const liveData = useAssetSubscription(asset, () => datapointNames);

	const assetLiveData = React.useMemo(() => {
		return asset ? liveData : {};
	}, [asset, liveData]);

	const createPaginatedItems = (valueItems: Array<InfoWidgetValueItem>) => {
		return config.settings.items.map((item: InfoWidgetItem) => {
			const identifier = getItemIdentifier(item);
			const valueItem = valueItems.find(
				(valueItem) => valueItem.type === item.type && valueItem.identifier === identifier
			);
			return {
				item,
				type: item.type,
				key: getItemKey(item),
				value: valueItem ? valueItem.value : NO_VALUE_DISPLAY
			};
		});
	};

	const getInitialValue = (item: InfoWidgetItem): any => {
		if (isInfoWidgetDatapoint(item)) {
			return '';
		} else if (isInfoWidgetProperty(item)) {
			const {propertyKey} = item;

			if (asset && propertyKey) {
				return asset.properties[propertyKey] ?? asset[propertyKey as keyof Asset];
			} else {
				return '';
			}
		}
	};

	const updateValues = (): Array<InfoWidgetValueItem> => {
		return config.settings.items.reduce((newValueItems: InfoWidgetValueItem[], item: InfoWidgetItem) => {
			const identifier = getItemIdentifier(item);
			if (item.type === InfoWidgetItemCategory.DATAPOINT && identifier) {
				const existingItem = state.valueItems.find(
					(valueItem) => valueItem.type === item.type && valueItem.identifier === identifier
				);

				const value =
					assetLiveData[identifier] !== undefined ? assetLiveData[identifier] : existingItem?.value || '';

				newValueItems.push({
					type: item.type,
					identifier,
					value
				});
			} else if (item.type === InfoWidgetItemCategory.PROPERTY && identifier) {
				const value = getInitialValue(item);
				newValueItems.push({
					type: item.type,
					identifier,
					value
				});
			}
			return newValueItems;
		}, []);
	};

	React.useEffect(() => {
		const updatedValueItems = updateValues();
		setState({
			valueItems: updatedValueItems,
			paginatedItems: config.settings.paginated ? createPaginatedItems(updatedValueItems) : []
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetLiveData, config.settings.paginated]);

	const {headline, items, paginated, rows, filterable} = config.settings;
	const {valueItems, paginatedItems} = state;
	const showHeadline = headline && headline.length > 0;
	const containerClassName = classNames({
		info_widget_item_container: true,
		no_title: !showHeadline
	});

	return (
		<div className="info-widget">
			{showHeadline && <h3 className="info-widget-headline">{translateCustomTokens(headline, t)}</h3>}
			{paginated ? (
				<InfoWidgetPaginated
					items={items}
					values={paginatedItems}
					rows={rows}
					filterable={filterable}
					t={t}
					className={containerClassName}
				/>
			) : (
				<InfoWidgetRegular items={items} t={t} values={valueItems} className={containerClassName} />
			)}
		</div>
	);
};

export default InfoWidget;
